/* eslint-disable @typescript-eslint/no-explicit-any */
import { Moment } from 'moment';
import axios, { AxiosRequestConfig } from 'axios';


// This is used check is NODE ENV is development or not
const isDev = process.env['NODE_ENV'] === 'development';

// Store field key constants
export const DESTINATION = 'destination';
export const ROOMS_AND_GUESTS = 'roomsAndGuests';
export const DATES = 'dates';
export const SPECIAL_RATES = 'specialRates';
export const USE_POINTS = 'usePoints';
export const RESET = 'reset';
export const DESTINATION_HIDDEN_FIELDS = 'destinationHiddenFields';
export const LATITUDE_AND_LONGITUDE = 'latitudeAndLongitude';

// URL constants
export const AVAILABILITY_URL = '/reservation/availability.mi?isSearch=false&showErrors=true';
export const AVAILABILITY_CALENDAR_URL = '/search/availabilityCalendar.mi?isRateCalendar=true&isSearch=true';
export const MODIFY_RLM_PAGE_URL = '/reservation/modifyRateListMenu.mi';

// Group inventory error key constant
export const GROUP_INVENTORY_UNAVAILABLE_ERR_MSG = 'group.rate.unavailable.redirect.rlm';
// Selected Currency key constant
export const SELECTED_CURRENCY = 'selectedCurrency';

export function getDTTFlag() {
  return typeof window !== 'undefined' ? (window?.['dtt'] as unknown as boolean) ?? false : false;
}

// This is used check the common headers ie: DTT Flag
const commonHeaders = () => {
  const dtt = getDTTFlag();
  return {
    'Content-Type': 'application/json',
    ...(dtt ? { dtt } : {}),
  };
};

const instance = axios.create({
  headers: commonHeaders(),
  ...(isDev && {
    baseURL: process.env['NEXT_PUBLIC_PROXY_HOST'] || '',
  }),
});

// eslint-disable-next-line @typescript-eslint/no-var-requires
const objectMapper = require('object-mapper');

export const transformResponse = (result: any, mapObject: Record<string, string>) => {
  return objectMapper(result, mapObject) || {};
};

export const axiosWrapper = {
  intercept: (interceptConfigs: Record<string, string | boolean | number>) => {
    if (Object.keys(interceptConfigs)?.length) {
      for (const key in interceptConfigs) {
        instance.defaults.headers.common[key] = interceptConfigs[key] || '';
      }
    }
  },
  get: <T = any>(url: string, config?: AxiosRequestConfig) => {
    const customHeaders = {
      ...instance.defaults.headers.get,
      ...commonHeaders(),
      ...config?.headers,
    };

    return instance.get<T>(url, { ...config, headers: customHeaders });
  },
  post(url: string, data?: any, config?: AxiosRequestConfig) {
    const customHeaders = {
      ...instance.defaults.headers.post,
      ...commonHeaders(),
      ...config?.headers,
    };

    return instance.post(url, data, { ...config, headers: customHeaders });
  },
  put: (url: string, data?: any, config?: AxiosRequestConfig) => {
    const customHeaders = {
      ...instance.defaults.headers.put,
      ...commonHeaders(),
      ...config?.headers,
    };

    return instance.put(url, data, { ...config, headers: customHeaders });
  },
  delete: (url: string, config?: AxiosRequestConfig) => {
    const customHeaders = {
      ...instance.defaults.headers.delete,
      ...commonHeaders(),
      ...config?.headers,
    };

    return instance.delete(url, { ...config, headers: customHeaders });
  },
};

export const getFromSession = (key: string) => {
  return canUseDOM && sessionStorage.getItem(key);
};

/**
 *
 * @param url as str
 * This is used find the nth occurence of a char in string
 */
const findNthOccurence = (str: string, nth: number, char: string) => {
  let index = 0;
  for (let i = 0; i < nth; i += 1) {
    if (index !== -1) index = str.indexOf(char, index + 1);
  }
  return index;
};

/**
 *
 * @param url
 * This is used for prefix the url. This will check for subdirectory prefix
 * if present then it will be prefixed otherwise the url is returned without prefix.
 */
export function addSubDirectoryPrefix(url: string) {
  const SUBDIRECTORY_PREFIX = getFromSession('SUBDIRECTORYCONTEXTPATH') || '';
  if (
    SUBDIRECTORY_PREFIX &&
    url?.startsWith('/') &&
    url?.substring(1, findNthOccurence(url, 1, '/'))?.toLowerCase() !== SUBDIRECTORY_PREFIX?.toLowerCase()
  ) {
    return SUBDIRECTORY_PREFIX ? '/' + SUBDIRECTORY_PREFIX + url : url;
  } else return url;
}

/**
 *
 * @param url
 * This is used for navigation from current to another page. This will check for subdirectory prefix
 * if present then it will be prefixed and redirected otherwise the redirected to url.
 */
export const redirect = (url: string) => {
  if (url) {
    url = addSubDirectoryPrefix(url);
    if (isDev) url = url.replace('.mi', '');
    window.location.assign(url);
  }
};

export const canUseDOM = !!(typeof window !== 'undefined' && window.document);

export function getWindowSession(): any {
  // if (canUseDOM) {
  //   return window['sessionData' as any] ?? {};
  // }
  let data;
  if (canUseDOM) {
    data = window['sessionDataClient' as any] ?? window['sessionData'];
    if (!data) {
      data = globalThis.window['sessionDataClient' as any] ?? globalThis.window['sessionData'];
    }
  }
  return data ?? {};
}

// Destination input structure
export interface DestinationFieldType {
  displayText: string;
  pid?: string;
}

// Room and guest selection data
export interface RoomsAndGuestsFieldType {
  roomsCount: string;
  adultsCount?: number;
  childrenCount?: number;
  childrenAges?: Array<string>;
}
// Latitude/longitude coordinate payload
export interface LatitudeLongitudeType {
  latitude: string;
  longitude: string;
}

// Points redemption toggle
export interface UsePointsFieldType {
  value: boolean;
}

// Date selection payload
export interface DatesFieldType {
  lengthOfStay?: number;
  lastDefaultDate?: string;
  fromDate?: DateObjectType;
  toDate?: DateObjectType;
  flexible?: string;
  checkin?: string;
  checkout?: string;
}

// Special rate configuration
export interface SpecialRatesFieldType {
  displayText: string;
  clusterCode: string;
  corporateCode: string;
  groupCode?: string;
}

// Hidden destination metadata used for search
export interface DestinationHiddenFieldsType {
  types?: string;
  latitude?: string;
  longitude?: string;
  state?: string;
  address?: string;
  city?: string;
  country?: string;
  website?: string;
  radius?: string;
  placeId?: string;
  primaryDescription?: string;
  secondaryDescription?: string;
  postalAddress?: string;
}
// Aggregated store values used for search submission
export interface StoreValuesType {
  [DESTINATION]?: DestinationFieldType;
  [ROOMS_AND_GUESTS]?: RoomsAndGuestsFieldType;
  [DATES]?: DatesFieldType;
  [USE_POINTS]?: UsePointsFieldType;
  [SPECIAL_RATES]?: SpecialRatesFieldType;
  [DESTINATION_HIDDEN_FIELDS]?: DestinationHiddenFieldsType;
  [LATITUDE_AND_LONGITUDE]?: LatitudeLongitudeType;
}

// Normalized session document contract used by search and reservation flows
export interface SessionDocumentType {
  data: {
    AriesReservation: {
      numberOfRooms: number;
      selectedCurrency: string;
      dataLayerProperties: {
        res_booking_room_Count: string;
        res_room_nights_total: string;
        res_rooms_per_night: string;
      };
      availabilityRequestVO: {
        checkInDate: moment.Moment | string | undefined;
        checkOutDate: moment.Moment | string | undefined;
        lengthOfStay: number | undefined;
        rewardsRedemption: boolean | undefined;
        numRooms: number | undefined;
        numGuestsPerRoom: number | undefined;
        numAdultsPerRoom: number | undefined;
        numChildrenPerRoom: number | undefined;
        childrenAges: string | undefined;
        flexibleDate?: boolean | undefined;
        clusterCode?: string | undefined;
        corporateCode?: string | undefined;
        groupCode?: string | undefined;
      };
      errorMessages: {
        availabilitySearchFormErrorMessages: {
          errorMessages: [];
          // eslint-disable-next-line @typescript-eslint/ban-types
          errorFields: {};
          errorMessageKeys: string[];
        };
      };
    };
    AriesSearch: {
      search_google_places_destination: string;
      search_keyword: string | undefined;
      searchCriteria: {
        address: {
          city: string | undefined;
          country: string | undefined;
          latitude: string | undefined;
          longitude: string | undefined;
          destination: string | undefined;
          stateProvince: string | undefined;
        };
        geoCode: string;
        availabilityRequestVO: {
          checkInDate: moment.Moment | string | undefined;
          checkOutDate: moment.Moment | string | undefined;
          lengthOfStay: number | undefined;
          rewardsRedemption: boolean | undefined;
          numRooms: number | undefined;
          numGuestsPerRoom: number | undefined;
          numAdultsPerRoom: number | undefined;
          numChildrenPerRoom: number | undefined;
          childrenAges: string | undefined;
          flexibleDate?: boolean | undefined;
          clusterCode?: string | undefined;
          corporateCode?: string | undefined;
          groupCode?: string | undefined;
        };
        destinationAddressPlaceId: string | undefined;
        destinationAddressMainText: string | undefined;
        destinationAddressSecondaryText: string;
        selectedCurrency: string;
        showFullPrice: string;
      };
      currentConversionRateDecimalPoint?: any;
      currentConversionRate?: any;
    };
    AriesCommon: {
      search_cluster_code: string | undefined;
    };
  };
}

// Alias for moment date type
export type DateObjectType = Moment;

// Date formatting constants
export const DATE_FORMAT = 'MM/DD/YYYY';
export const INVALID_ROOM_COUNT = 'room.count.between3and10.invalid';
export const IS_LOCALE_ARABIC = 'ar';
export const IS_LOCALE_HINDI = 'hi';
export const ISO_FORMAT = 'YYYY-MM-DDT00:00:00.000+0000';

// Mapping for extracting relevant session field
export const SessionDataMap = {
  'cacheData.data.AriesReservation.tripsXRequestedByHeader': 'tripsXRequestedByHeader',
  'cacheData.data.AriesReservation.tripId': 'tripId',
  'cacheData.data.AriesReservation.itemId': 'itemId',
  'cacheData.data.AriesReservation.isRewardsRedemptionFlow': 'redemption',
  'cacheData.data.AriesReservation.propertyId': 'propertyId',
  'cacheData.data.AriesSearch.searchCriteria.showFullPrice': 'showFullPrice',
  'cacheData.data.AriesSearch.searchCriteria.selectedCurrency': 'selectedCurrency',
};

// Converts Arabic numerals in a date string to English numerals
export const convertArabicToEnglishNumbers = (dateString: string): string => {
  // Arabic to English numeral mapping
  const arabicToEnglishMap: { [index: string]: string } = {
    '٠': '0',
    '١': '1',
    '٢': '2',
    '٣': '3',
    '٤': '4',
    '٥': '5',
    '٦': '6',
    '٧': '7',
    '٨': '8',
    '٩': '9',
  };

  // Replace Arabic numerals with English numerals
  return dateString.replace(/[٠-٩]/g, function (match) {
    return arabicToEnglishMap[match].toString();
  });
};

// Converts Hindi (Devanagari) numerals in a string to English numerals
  export const convertHindiToEnglishNumbers = (input: string): string => {
  // Hindi (Devanagari) to English numeral mapping
  const hindiToEnglishMap: { [key: string]: string } = {
    '०': '0',
    '१': '1',
    '२': '2',
    '३': '3',
    '४': '4',
    '५': '5',
    '६': '6',
    '७': '7',
    '८': '8',
    '९': '9',
  };

  // Replace Hindi numerals with English numerals
  return input.replace(/[०-९]/g, match => hindiToEnglishMap[match]);
};

// Returns the current locale code from the document language attribute
export const getCurrentLocale = (): string => {
  return document.documentElement?.lang?.split(/[_-]/)?.[0]?.toLowerCase() || 'en';
};

// Formats a date to ISO format while normalizing locale-specific numerals
export const getISODateFormatted = (date?: DateObjectType): string => {
  if (getCurrentLocale() === IS_LOCALE_ARABIC) {
    return convertArabicToEnglishNumbers(date?.format(ISO_FORMAT) ?? '');
  } else if (getCurrentLocale() === IS_LOCALE_HINDI) {
    return convertHindiToEnglishNumbers(date?.format(ISO_FORMAT) ?? '');
  } else {
    return date?.format(ISO_FORMAT) ?? '';
  }
}

// Updates session document with search data and triggers availability redirect
export async function updateSessionDoc(
  formValues: StoreValuesType | undefined,
  isFlexibleDateSearch?: boolean | undefined,
  taxAndCurrency?: any
): Promise<any> {
  // TODO - build an endpoint generator and replace this URL with method call.
  try {
    const sessionData = await getWindowSession();
    const sessionDoc = sessionData?.cacheData ?? {};
    // Get currency stored in session storage
    const selectedCurrencyFromSession = getFromSession(SELECTED_CURRENCY);

    // Extract key reservation data
    const {
      propertyId,
      selectedCurrency = '',
      showFullPrice = false,
    } = transformResponse({ cacheData: sessionDoc }, SessionDataMap);

    // Update session search and reservation data
    updateSessionData(sessionDoc, formValues, isFlexibleDateSearch, taxAndCurrency);
    await axiosWrapper
      .post(`${process.env['NEXT_PUBLIC_PREFIX']}${process.env['NEXT_PUBLIC_UPDATE_SESSION_URL_V2']}`, {
        createOrUpdate: {
          AriesSearch: sessionDoc.data?.AriesSearch ?? {},
          AriesReservation: sessionDoc.data?.AriesReservation ?? {},
        },
      })
      .then(() => {
        // Redirect on successful session upda
        redirectToAvailabilityUrl(
          { formValues, isFlexibleDateSearch },
          propertyId,
          selectedCurrencyFromSession ?? selectedCurrency,
          showFullPrice,
          true
        );
      })
      .catch(() => {
        // Redirect with fallback if update fails
        redirectToAvailabilityUrl(
          { formValues, isFlexibleDateSearch },
          propertyId,
          selectedCurrencyFromSession ?? selectedCurrency,
          showFullPrice,
          false
        );
      });
  } catch (error) {
    return error;
  }
}

// Mutates AriesSearch and AriesReservation objects with latest request data
function updateSessionData(
  sessionDoc: SessionDocumentType,
  formValues: StoreValuesType | undefined,
  isFlexibleDateSearch?: boolean | undefined,
  taxAndCurrency?: any
): void {
  // Format check-in and check-out dates
  const checkinDate = getISODateFormatted(formValues?.dates?.fromDate);
  const checkoutDate = getISODateFormatted(formValues?.dates?.toDate);
  // Parse room count
  const roomCount = parseInt(formValues?.roomsAndGuests?.roomsCount ?? '0', 10);
  //  Read selected currency from session
  const selectedCurrency: string | null = getFromSession(SELECTED_CURRENCY) as string | null;
  // Normalize children age values
  let childrenAges = formValues?.roomsAndGuests?.childrenAges || [];
  childrenAges = childrenAges?.map((item: string) => (!item || item === '<1' || item === '-1' ? '0' : item));
  // Resolve address information
  const city = formValues?.destinationHiddenFields?.city ?? sessionDoc.data?.AriesSearch?.searchCriteria?.address?.city;
  const state =
    formValues?.destinationHiddenFields?.state ?? sessionDoc.data?.AriesSearch?.searchCriteria?.address?.stateProvince;
  const country =
    formValues?.destinationHiddenFields?.country ?? sessionDoc.data?.AriesSearch?.searchCriteria?.address?.country;
  const destination =
    formValues?.destination?.displayText || sessionDoc.data?.AriesSearch?.searchCriteria?.address?.destination;
  const pID = formValues?.destination?.pid || sessionDoc.data?.AriesSearch?.searchCriteria?.destinationAddressPlaceId;
  // Check for group inventory error in session
  const errorMessageKeys =
    sessionDoc?.data?.AriesReservation?.errorMessages?.availabilitySearchFormErrorMessages?.errorMessageKeys;
  const isGroupCodeInventoryUnavailableError = errorMessageKeys?.some(
    item => item === GROUP_INVENTORY_UNAVAILABLE_ERR_MSG
  );
  if (selectedCurrency) {
    sessionDoc.data.AriesReservation.selectedCurrency =
      selectedCurrency ?? sessionDoc.data.AriesReservation.selectedCurrency;
    sessionDoc.data.AriesSearch.searchCriteria.selectedCurrency =
      selectedCurrency ?? sessionDoc.data.AriesSearch.searchCriteria.selectedCurrency;
  }
  if (sessionDoc.data.AriesSearch) {
    sessionDoc.data.AriesSearch.search_google_places_destination = `"Name:${city}|Locality:${city}|State:${state}|Postal Code:|Country:${country}|Latitude:${formValues?.destinationHiddenFields?.latitude}|Longitude:${formValues?.destinationHiddenFields?.longitude}|Types:|Address:"`;
    sessionDoc.data.AriesSearch.search_keyword = destination;
    if (!sessionDoc.data.AriesSearch.searchCriteria?.address) {
      sessionDoc.data.AriesSearch.searchCriteria.address = {
        city: '',
        country: '',
        latitude: '',
        longitude: '',
        destination: '',
        stateProvince: '',
      };
    }
    sessionDoc.data.AriesSearch.searchCriteria.address.city = city;
    sessionDoc.data.AriesSearch.searchCriteria.address.country = country;
    sessionDoc.data.AriesSearch.searchCriteria.address.latitude = formValues?.destinationHiddenFields?.latitude;
    sessionDoc.data.AriesSearch.searchCriteria.address.longitude = formValues?.destinationHiddenFields?.longitude;
    sessionDoc.data.AriesSearch.searchCriteria.address.destination = destination;

    sessionDoc.data.AriesSearch.searchCriteria.geoCode = `${formValues?.destinationHiddenFields?.latitude},${formValues?.destinationHiddenFields?.longitude}`;

    sessionDoc.data.AriesSearch.searchCriteria.availabilityRequestVO.checkInDate = checkinDate;
    sessionDoc.data.AriesSearch.searchCriteria.availabilityRequestVO.checkOutDate = checkoutDate;
    sessionDoc.data.AriesSearch.searchCriteria.availabilityRequestVO.lengthOfStay = formValues?.dates?.toDate?.diff(
      formValues?.dates?.fromDate,
      'days'
    );
    sessionDoc.data.AriesSearch.searchCriteria.availabilityRequestVO.rewardsRedemption = formValues?.usePoints?.value;
    sessionDoc.data.AriesSearch.searchCriteria.availabilityRequestVO.clusterCode =
      formValues?.specialRates?.clusterCode;
    sessionDoc.data.AriesSearch.searchCriteria.availabilityRequestVO.corporateCode =
      formValues?.specialRates?.corporateCode;
    sessionDoc.data.AriesSearch.searchCriteria.availabilityRequestVO.groupCode = formValues?.specialRates?.groupCode;
    sessionDoc.data.AriesSearch.searchCriteria.availabilityRequestVO.numRooms = roomCount;
    sessionDoc.data.AriesSearch.searchCriteria.availabilityRequestVO.numGuestsPerRoom =
      formValues?.roomsAndGuests?.adultsCount;
    sessionDoc.data.AriesSearch.searchCriteria.availabilityRequestVO.flexibleDate = isFlexibleDateSearch;
    if (
      formValues?.roomsAndGuests?.childrenCount &&
      sessionDoc.data.AriesSearch.searchCriteria.availabilityRequestVO.numGuestsPerRoom
    ) {
      sessionDoc.data.AriesSearch.searchCriteria.availabilityRequestVO.numGuestsPerRoom +=
        formValues?.roomsAndGuests?.childrenCount;
    }
    sessionDoc.data.AriesSearch.searchCriteria.availabilityRequestVO.numAdultsPerRoom =
      formValues?.roomsAndGuests?.adultsCount;
    sessionDoc.data.AriesSearch.searchCriteria.availabilityRequestVO.numChildrenPerRoom =
      formValues?.roomsAndGuests?.childrenCount;
    sessionDoc.data.AriesSearch.searchCriteria.availabilityRequestVO.childrenAges = childrenAges?.join(',');

    sessionDoc.data.AriesSearch.searchCriteria.destinationAddressPlaceId = pID;
    sessionDoc.data.AriesSearch.searchCriteria.destinationAddressMainText = city;
    sessionDoc.data.AriesSearch.searchCriteria.destinationAddressSecondaryText = `${state}, ${country}`;
    if (taxAndCurrency) {
      sessionDoc.data.AriesSearch.searchCriteria.showFullPrice = taxAndCurrency.includeTaxes;
      sessionDoc.data.AriesSearch.searchCriteria.selectedCurrency = taxAndCurrency.currCurrency;
    }
  }
  if (sessionDoc.data.AriesReservation?.availabilityRequestVO) {
    sessionDoc.data.AriesReservation.availabilityRequestVO.checkInDate = checkinDate;
    sessionDoc.data.AriesReservation.availabilityRequestVO.checkOutDate = checkoutDate;
    sessionDoc.data.AriesReservation.availabilityRequestVO.lengthOfStay = formValues?.dates?.toDate?.diff(
      formValues?.dates?.fromDate,
      'days'
    );
    sessionDoc.data.AriesReservation.availabilityRequestVO.rewardsRedemption = formValues?.usePoints?.value;
    sessionDoc.data.AriesReservation.availabilityRequestVO.clusterCode = formValues?.specialRates?.clusterCode;
    sessionDoc.data.AriesReservation.availabilityRequestVO.corporateCode = formValues?.specialRates?.corporateCode;
    sessionDoc.data.AriesReservation.availabilityRequestVO.groupCode = formValues?.specialRates?.groupCode;
    sessionDoc.data.AriesReservation.availabilityRequestVO.numRooms = roomCount;
    sessionDoc.data.AriesReservation.numberOfRooms = roomCount;
    sessionDoc.data.AriesReservation.availabilityRequestVO.numGuestsPerRoom = formValues?.roomsAndGuests?.adultsCount;
    sessionDoc.data.AriesReservation.availabilityRequestVO.flexibleDate = isFlexibleDateSearch;
    sessionDoc.data.AriesCommon.search_cluster_code = formValues?.specialRates?.clusterCode;
    if (
      formValues?.roomsAndGuests?.childrenCount &&
      sessionDoc.data.AriesReservation.availabilityRequestVO.numGuestsPerRoom
    ) {
      sessionDoc.data.AriesReservation.availabilityRequestVO.numGuestsPerRoom +=
        formValues?.roomsAndGuests?.childrenCount;
    }
    sessionDoc.data.AriesReservation.availabilityRequestVO.numAdultsPerRoom = formValues?.roomsAndGuests?.adultsCount;
    sessionDoc.data.AriesReservation.availabilityRequestVO.numChildrenPerRoom =
      formValues?.roomsAndGuests?.childrenCount;
    sessionDoc.data.AriesReservation.availabilityRequestVO.childrenAges = childrenAges?.join(',');
  }
  if (isGroupCodeInventoryUnavailableError) {
    sessionDoc.data.AriesReservation.errorMessages.availabilitySearchFormErrorMessages.errorMessageKeys =
      errorMessageKeys.filter(key => key !== GROUP_INVENTORY_UNAVAILABLE_ERR_MSG);
  }
  if (sessionDoc.data.AriesReservation && roomCount > 3) {
    sessionDoc.data.AriesReservation.errorMessages = {
      availabilitySearchFormErrorMessages: {
        errorMessages: [],
        // eslint-disable-next-line @typescript-eslint/ban-types
        errorFields: {},
        errorMessageKeys: [INVALID_ROOM_COUNT],
      },
    };
    sessionDoc.data.AriesReservation.dataLayerProperties.res_booking_room_Count = `${roomCount}`;
    sessionDoc.data.AriesReservation.dataLayerProperties.res_room_nights_total = `${roomCount}`;
    sessionDoc.data.AriesReservation.dataLayerProperties.res_rooms_per_night = `${roomCount}`;
  }
}

// Builds availability URL and redirects based on room count and search type
export const redirectToAvailabilityUrl = (
  data: { formValues: any; isFlexibleDateSearch: any },
  propertyId: any,
  selectedCurrency: any,
  showFullPrice: any,
  shouldRedirect: boolean
) => {
  const values = data.formValues;
  const roomCount = parseInt(values.roomsAndGuests?.roomsCount, 10);
  const { childrenCount } = values.roomsAndGuests ?? {};
  let childrenAges = values.roomsAndGuests?.childrenAges;

  if (childrenCount > 0) {
    childrenAges = childrenAges.map((item: string) => {
      return item === '-1' ? '0' : item;
    });
  }

  if (roomCount > 3 && !window.location.pathname?.includes(MODIFY_RLM_PAGE_URL)) {
    const redirect_url = `${AVAILABILITY_URL}&fromDate=${encodeURIComponent(
      values.dates?.fromDate?.format(DATE_FORMAT)
    )}&roomCount=${roomCount}&numberOfRooms=${roomCount}&useRequestCriteria=true&propertyCode=${propertyId}&toDate=${encodeURIComponent(
      values.dates?.toDate?.format(DATE_FORMAT)
    )}&flexibleDateSearch=${data.isFlexibleDateSearch}&numAdultsPerRoom=${
      values.roomsAndGuests?.adultsCount
    }&useRewardsPoints=${values.usePoints.value}&clusterCode=${
      values.specialRates?.clusterCode
    }&childrenCount=${childrenCount}&childrenAges=${encodeURIComponent(childrenAges?.join())}`;

    if (roomCount < 10) {
      redirect(redirect_url);
    }
  } else if (shouldRedirect) {
    if (data.isFlexibleDateSearch) {
      redirect(
        `${AVAILABILITY_CALENDAR_URL}&propertyCode=${propertyId}&currency=${selectedCurrency}&showFullPrice=${showFullPrice}`
      );
    } else {
      window?.location?.replace(window.location.pathname);
    }
  }
};